import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@environment';
import { Observable } from 'rxjs';

export enum PushNotificationTargetType {
  Reservation = 'Reservation',
  Water = 'Water',
  ArrivalDate = 'ArrivalDate',
  User = 'User',
  Global = 'Global',
}

export interface PushNotificationTarget {
  type: PushNotificationTargetType;
  reservationNumber?: string;
  waterId?: number;
  arrivalDateFrom?: string;
  arrivalDateTo?: string;
  userEmail?: string;
}

export interface SendPushNotificationRequest {
  titleNl: string;
  titleEn: string;
  titleDe: string;
  titleIt: string;
  titleFr: string;
  bodyNl: string;
  bodyEn: string;
  bodyDe: string;
  bodyIt: string;
  bodyFr: string;
  targets: PushNotificationTarget[];
}

export interface SendPushNotificationResponse {
  usersTargeted: number;
  tokensTargeted: number;
  successCount: number;
  failureCount: number;
  errors: string[];
}

export interface TranslatePushNotificationRequest {
  from: string;
  to: string[];
  title: string;
  body: string;
}

export interface TranslatePushNotificationResponse {
  success: boolean;
  translations?: Record<string, { title: string; body: string }>;
  message?: string;
}

export interface GetUserCountsRequest {
  targets: PushNotificationTarget[];
}

export interface GetUserCountsResponse {
  // example: { "nl": 15, "en": 23, "de": 8, "it": 0, "fr": 5 }
  userCounts: Record<string, number>; // Total user counts per language (all users, regardless of push token)
  pushTokenCounts: Record<string, number>; // Push-enabled user counts per language
  // Informational only (does not affect targeting): distribution of reservations arriving in the
  // selected date range by their site locale. Empty when no ArrivalDate target is present.
  // example: { "nl": 20, "de": 9 }
  reservationSiteCounts: Record<string, number>;
}

@Injectable({
  providedIn: 'root',
})
export class PushNotificationsService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.apiUrl;

  sendNotification(request: SendPushNotificationRequest): Observable<SendPushNotificationResponse> {
    return this.http.post<SendPushNotificationResponse>(`${this.baseUrl}/api/push-notifications/send`, request);
  }

  translateNotification(request: TranslatePushNotificationRequest): Observable<TranslatePushNotificationResponse> {
    return this.http.post<TranslatePushNotificationResponse>(
      `${this.baseUrl}/api/push-notifications/translate`,
      request,
    );
  }

  getUserCounts(request: GetUserCountsRequest): Observable<GetUserCountsResponse> {
    return this.http.post<GetUserCountsResponse>(`${this.baseUrl}/api/push-notifications/user-counts`, request);
  }
}
